import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import ChatbotContext from '../../../context/ChatbotContext/ChatbotContext';
import UserContext from '../../../context/UserContext/UserContext';
import useAuthRoles from '../../../hooks/useAuthRoles';
import useChatbotCreation from '../../../hooks/useChatbotCreation';
import BotBuilderService from '../../../services/BotBuilderService/BotBuilderService';
import OrganisationService from '../../../services/OrganisationService/OrganisationService';
import Chatbot from '../../../types/Chatbot';
import Organisation from '../../../types/Organisation';
import ChatbotSelector from '../ChatbotSelector/ChatbotSelector';
import CreateEntityModal from '../CreateEntityModal/CreateEntityModal';
import OrganisationSelector from '../OrganisationSelector/OrganisationSelector';

interface OrgChatbotSwitcherProps {
  collapsed?: boolean;
}

function OrgChatbotSwitcher({
  collapsed = false
}: Readonly<OrgChatbotSwitcherProps>): React.ReactElement | null {
  const { user } = useContext(UserContext);
  const {
    currentChatbot,
    setCurrentChatbot,
    selectedOrganisation,
    setSelectedOrganisation,
    chatbotsRefreshKey
  } = useContext(ChatbotContext);
  const { userIsJapetoAdmin } = useAuthRoles();
  const isAdmin = userIsJapetoAdmin();
  const { createChatbot } = useChatbotCreation();

  const [chatbots, setChatbots] = useState<Chatbot[]>([]);
  const [loadingChatbots, setLoadingChatbots] = useState(false);
  const [showCreateOrganisation, setShowCreateOrganisation] = useState(false);
  const [showCreateChatbot, setShowCreateChatbot] = useState(false);
  const autoSelectFirstRef = useRef(false);

  const loadChatbots = useCallback(
    (organisationId: number, signal?: AbortSignal): Promise<Chatbot[]> => {
      setLoadingChatbots(true);
      return BotBuilderService.getChatbotsInfo(organisationId, signal)
        .then((bots) => {
          setChatbots(bots);
          return bots;
        })
        .catch(() => {
          if (!signal?.aborted) {
            setChatbots((prev) => (prev.length === 0 ? prev : []));
          }
          return [];
        })
        .finally(() => {
          if (!signal?.aborted) {
            setLoadingChatbots(false);
          }
        });
    },
    []
  );

  useEffect(() => {
    if (!isAdmin || selectedOrganisation || !currentChatbot) return;
    OrganisationService.getOrganisation(currentChatbot.organisationId)
      .then(setSelectedOrganisation)
      .catch(() =>
        setSelectedOrganisation({
          id: currentChatbot.organisationId,
          name: ''
        })
      );
  }, [isAdmin, currentChatbot, selectedOrganisation, setSelectedOrganisation]);

  useEffect(() => {
    if (isAdmin || !user || selectedOrganisation || user.organisationId == null)
      return;
    setSelectedOrganisation({ id: user.organisationId, name: '' });
  }, [isAdmin, user, selectedOrganisation, setSelectedOrganisation]);

  useEffect(() => {
    if (!selectedOrganisation) return undefined;

    const controller = new AbortController();
    loadChatbots(selectedOrganisation.id, controller.signal).then((bots) => {
      if (controller.signal.aborted || !autoSelectFirstRef.current) return;
      autoSelectFirstRef.current = false;
      setCurrentChatbot(bots.length > 0 ? bots[0].id : null);
    });

    return (): void => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrganisation?.id, loadChatbots, setCurrentChatbot]);

  useEffect(() => {
    if (!selectedOrganisation || !currentChatbot) return;
    if (currentChatbot.organisationId !== selectedOrganisation.id) return;
    if (chatbots.some((bot) => bot.id === currentChatbot.id)) return;
    loadChatbots(selectedOrganisation.id);
  }, [currentChatbot, selectedOrganisation, chatbots, loadChatbots]);

  useEffect(() => {
    if (!selectedOrganisation) return;
    loadChatbots(selectedOrganisation.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatbotsRefreshKey]);

  const handleOrganisationSelect = (organisation: Organisation): void => {
    if (organisation.id === selectedOrganisation?.id) return;
    autoSelectFirstRef.current = true;
    setSelectedOrganisation(organisation);
  };

  const handleCreateOrganisation = async (name: string): Promise<void> => {
    const organisation = await OrganisationService.createOrganisation(name);
    autoSelectFirstRef.current = true;
    setSelectedOrganisation(organisation);
    setShowCreateOrganisation(false);
  };

  const openCreateChatbot = (): void => setShowCreateChatbot(true);

  const handleCreateChatbot = async (name: string): Promise<void> => {
    await createChatbot(name);
    setShowCreateChatbot(false);
  };

  if (!user) return null;

  return (
    <div
      className={
        collapsed
          ? 'flex flex-col items-center gap-2 mt-2 mb-4'
          : 'w-full flex flex-col gap-1'
      }
    >
      {isAdmin && (
        <OrganisationSelector
          selectedOrganisation={selectedOrganisation}
          onSelect={handleOrganisationSelect}
          onCreate={() => setShowCreateOrganisation(true)}
          collapsed={collapsed}
        />
      )}
      <ChatbotSelector
        chatbots={chatbots}
        loading={loadingChatbots}
        onCreate={
          isAdmin && selectedOrganisation ? openCreateChatbot : undefined
        }
        collapsed={collapsed}
      />

      {isAdmin && (
        <>
          <CreateEntityModal
            open={showCreateOrganisation}
            onClose={() => setShowCreateOrganisation(false)}
            title='Create organisation'
            label='Organisation name'
            placeholder='Type organisation name'
            onCreate={handleCreateOrganisation}
          />
          <CreateEntityModal
            open={showCreateChatbot}
            onClose={() => setShowCreateChatbot(false)}
            title='Create chatbot'
            label='Chatbot name'
            placeholder='Type chatbot name'
            onCreate={handleCreateChatbot}
          />
        </>
      )}
    </div>
  );
}

export default OrgChatbotSwitcher;
